import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { LayoutDashboard, Search, CreditCard, Settings, LogOut, Rocket, Menu, X, Sun, Moon } from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/onboarding', label: 'Einrichtung', icon: Rocket, onboardingOnly: true },
  { path: '/discovery', label: 'Entdeckung', icon: Search },
  { path: '/billing', label: 'Abrechnung', icon: CreditCard },
  { path: '/settings', label: 'Einstellungen', icon: Settings },
];

export default function Layout({ children }) {
  const { user, company, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('theme') === 'dark');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);

  const filteredNav = navItems.filter(item => {
    if (item.onboardingOnly && company?.onboarding_completed) return false;
    return true;
  });

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="min-h-screen flex bg-background" data-testid="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-64 flex flex-col border-r border-border bg-card transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="sidebar">
        <div className="p-5 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="LaizyLeads" className="h-8 w-auto" />
            <span className="font-semibold text-lg tracking-tight">LaizyLeads</span>
          </Link>
          {company && (
            <p className="text-xs text-muted-foreground mt-2 truncate">{company.name}</p>
          )}
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {filteredNav.map(item => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                onClick={() => setSidebarOpen(false)}>
                <Icon className="w-4.5 h-4.5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-border space-y-2">
          <div className="flex items-center justify-between px-3 py-1">
            <span className="text-xs text-muted-foreground">Design</span>
            <Button variant="ghost" size="sm" onClick={() => setDark(!dark)} data-testid="theme-toggle" className="h-8 w-8 p-0">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="h-8 w-8 p-0">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        <div className="sticky top-0 z-20 lg:hidden flex items-center gap-3 p-4 border-b border-border bg-card/80 backdrop-blur-lg">
          <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn" className="h-9 w-9 p-0">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <span className="font-semibold">LaizyLeads</span>
        </div>
        <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
